import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, Download, Mail, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { SearchInput, PaginationBar } from "@/components/admin/ListControls";
import { useDebounced } from "@/hooks/useDebounced";
import { CASE_STATUS_LABELS, CASE_STATUS_KEYS, PROFILE_LABELS, formatDate } from "@/constants/business_status";

function EmailHealthBanner({ health, onRetry, retrying }) {
  if (!health || !health.needs_attention) return null;
  const parts = [];
  if (!health.configured) parts.push("Service email non configuré");
  if (health.pending > 0) parts.push(`${health.pending} email(s) en attente`);
  if (health.failed > 0) parts.push(`${health.failed} envoi(s) en échec`);
  return (
    <div data-testid="email-health-banner" className="flex items-center gap-3 bg-orange/10 border border-orange/40 text-navy rounded-xl px-4 py-3 mb-5">
      <AlertTriangle className="w-5 h-5 text-orange" />
      <div className="flex-1 text-sm">
        <span className="font-semibold">Service email : </span>
        <span data-testid="email-health-status">{parts.join(" • ")}</span>
      </div>
      <Button data-testid="email-health-retry" size="sm" variant="outline" onClick={onRetry} disabled={retrying || !health.configured} className="rounded-full font-semibold">
        {retrying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
        Relancer
      </Button>
    </div>
  );
}

export default function CasesPage() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const q = useDebounced(searchInput, 350);
  const [status, setStatus] = useState("all");
  const [profile, setProfile] = useState("all");
  const [hasOrg, setHasOrg] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [retrying, setRetrying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const initial = useRef(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (profile !== "all") params.profile_type = profile;
      if (hasOrg !== "all") params.has_organization = hasOrg === "yes";
      if (includeArchived) params.include_archived = true;
      const [casesRes, healthRes] = await Promise.all([
        apiClient.get("/admin/cases", { params }),
        apiClient.get("/admin/email-health").catch(() => ({ data: null })),
      ]);
      setItems(casesRes.data.items);
      setTotal(casesRes.data.total);
      setTotalPages(casesRes.data.total_pages);
      setHealth(healthRes.data);
    } catch {
      toast.error("Erreur lors du chargement des dossiers");
    } finally {
      setLoading(false);
    }
  }, [q, status, profile, hasOrg, includeArchived, page, pageSize]);

  useEffect(() => { if (admin) fetchData(); }, [admin, fetchData]);
  useEffect(() => {
    if (initial.current) { initial.current = false; return; }
    setPage(1);
  }, [q, status, profile, hasOrg, includeArchived, pageSize]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  const retryEmails = async () => {
    setRetrying(true);
    try {
      const r = await apiClient.post("/admin/emails/retry");
      toast.success(`Relance : ${r.data.sent} envoyé(s), ${r.data.failed} échec(s)`);
      const h = await apiClient.get("/admin/email-health");
      setHealth(h.data);
    } catch { toast.error("Erreur relance"); } finally { setRetrying(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.download = "demandes-express-delivery.csv"; link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur export"); } finally { setExporting(false); }
  };

  return (
    <AdminShell>
      <EmailHealthBanner health={health} onRetry={retryEmails} retrying={retrying} />

      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <SearchInput value={searchInput} onChange={setSearchInput} testId="cases-search-input" placeholder="Rechercher dossier, contact, organisation…" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="cases-filter-status" className="w-48 rounded-xl bg-white"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {CASE_STATUS_KEYS.map((s) => <SelectItem key={s} value={s}>{CASE_STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={profile} onValueChange={setProfile}>
          <SelectTrigger data-testid="cases-filter-profile" className="w-40 rounded-xl bg-white"><SelectValue placeholder="Profil" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous profils</SelectItem>
            {Object.entries(PROFILE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={hasOrg} onValueChange={setHasOrg}>
          <SelectTrigger data-testid="cases-filter-org" className="w-44 rounded-xl bg-white"><SelectValue placeholder="Organisation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Organisation ?</SelectItem>
            <SelectItem value="yes">Avec organisation</SelectItem>
            <SelectItem value="no">Sans organisation</SelectItem>
          </SelectContent>
        </Select>
        <Select value={includeArchived ? "yes" : "no"} onValueChange={(v) => setIncludeArchived(v === "yes")}>
          <SelectTrigger data-testid="cases-filter-archived" className="w-44 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">Actifs uniquement</SelectItem>
            <SelectItem value="yes">Inclure archivés</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="cases-refresh" onClick={fetchData} variant="outline" className="rounded-xl"><RefreshCw className="w-4 h-4" /></Button>
        <Button data-testid="cases-export" onClick={exportCsv} disabled={exporting} variant="outline" className="rounded-xl font-semibold">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} Exporter CSV
        </Button>
        <Button data-testid="cases-new" onClick={() => navigate("/admin/dossiers/nouveau")} className="bg-orange hover:bg-orange/90 text-white rounded-xl font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Nouveau dossier
        </Button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <Table data-testid="cases-table">
          <TableHeader>
            <TableRow>
              <TableHead>Dossier</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Prestation</TableHead>
              <TableHead>Trajet</TableHead>
              <TableHead>Date souhaitée</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Créé</TableHead>
              <TableHead>Prix</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground" data-testid="cases-empty">Aucun dossier pour ce filtre</TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} data-testid={`case-row-${c.id}`}>
                <TableCell className="text-xs font-semibold text-navy whitespace-nowrap">{c.case_number || "—"}</TableCell>
                <TableCell className="font-medium">{c.contact_summary?.display_name || "—"}<div className="text-xs text-muted-foreground">{c.contact_summary?.phone}</div></TableCell>
                <TableCell className="text-xs">{c.organization_summary?.legal_name || "—"}</TableCell>
                <TableCell className="text-xs">{c.service_type || <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-xs">
                  {c.origin_address?.city || "—"} → {c.destination_address?.city || "—"}
                </TableCell>
                <TableCell className="text-xs">{c.requested_date || "—"}</TableCell>
                <TableCell><span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1 whitespace-nowrap">{CASE_STATUS_LABELS[c.status] || c.status}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDate(c.created_at)}</TableCell>
                <TableCell className="text-xs">{c.proposed_price || "—"}</TableCell>
                <TableCell><Button data-testid={`case-view-${c.id}`} onClick={() => navigate(`/admin/dossiers/${c.id}`)} variant="ghost" size="sm" className="text-navy font-semibold">Voir</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PaginationBar total={total} page={page} pageSize={pageSize} totalPages={totalPages} onPage={setPage} onSize={setPageSize} loading={loading} testPrefix="cases-" />
    </AdminShell>
  );
}
